import React, { FC, useState } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { Tag } from '../hooks/useChatGraphQL'
import {useIntl} from 'react-intl'
import {CHAT_MESSAGES as C} from '../chatMessages'

type Props = {
    show: boolean
    onHide: () => void
    contactId: number
    currentTags: Tag[]
    availableTags: Tag[]
    onCreateTag: (name: string, color: string) => Promise<any>
    onManageTag: (contactId: number, tagId: number, action: 'add' | 'remove') => Promise<any>
}

const COLORS = [
    '#3E97FF', // Blue
    '#F1416C', // Red
    '#50CD89', // Green
    '#FFc700', // Yellow
    '#7239EA', // Purple
    '#009EF7', // Light Blue
    '#181C32', // Dark
]

const TagManager: FC<Props> = ({
    show,
    onHide,
    contactId,
    currentTags,
    availableTags,
    onCreateTag,
    onManageTag
}) => {
  const intl = useIntl()
    const [view, setView] = useState<'list' | 'create'>('list')
    const [newTagName, setNewTagName] = useState('')
    const [newTagColor, setNewTagColor] = useState(COLORS[0])
    const [submitting, setSubmitting] = useState(false)

    const handleCreate = async () => {
        if (!newTagName) return
        setSubmitting(true)
        try {
            await onCreateTag(newTagName, newTagColor)
            setView('list')
            setNewTagName('')
            setNewTagColor(COLORS[0])
        } catch (e) {
            // Error handled in hook or could set local error
        } finally {
            setSubmitting(false)
        }
    }

    const toggleTag = async (tag: Tag) => {
        const isAssigned = currentTags.some(t => t.id === tag.id)
        const action = isAssigned ? 'remove' : 'add'

        try {
            await onManageTag(contactId, tag.id, action)
        } catch (e) {
            // Error
        }
    }

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {intl.formatMessage(view === 'list' ? C.manageTags : C.newTag)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {view === 'list' ? (
                    <>
                        <div className='mb-5'>
                            <label className='form-label fw-bold'>{intl.formatMessage(C.availableTags)}</label>
                            <div className='d-flex flex-wrap gap-2'>
                                {availableTags.map(tag => {
                                    const isAssigned = currentTags.some(t => t.id === tag.id)
                                    return (
                                        <span
                                            key={tag.id}
                                            className={`badge cursor-pointer px-3 py-2 ${isAssigned ? '' : 'bg-light text-gray-600 border border-gray-300'}`}
                                            style={isAssigned ? { backgroundColor: tag.color, color: 'white' } : {}}
                                            onClick={() => toggleTag(tag)}
                                        >
                                            {tag.name}
                                            {isAssigned && <i className='bi bi-check text-white ms-1'></i>}
                                        </span>
                                    )
                                })}
                                {availableTags.length === 0 && (
                                    <span className='text-muted'>{intl.formatMessage(C.noTagsYet)}</span>
                                )}
                            </div>
                        </div>

                        <div className='d-grid'>
                            <button className='btn btn-light-primary btn-sm' onClick={() => setView('create')}>
                                <i className='bi bi-plus-lg'></i> {intl.formatMessage(C.createNewTag)}
                            </button>
                        </div>
                    </>
                ) : (
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>{intl.formatMessage(C.name)}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={intl.formatMessage(C.tagNamePlaceholder)}
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{intl.formatMessage(C.color)}</Form.Label>
                            <div className='d-flex gap-2 flex-wrap'>
                                {COLORS.map(color => (
                                    <div
                                        key={color}
                                        className={`rounded-circle cursor-pointer ${newTagColor === color ? 'border border-3 border-dark' : ''}`}
                                        style={{ width: '30px', height: '30px', backgroundColor: color }}
                                        onClick={() => setNewTagColor(color)}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                {view === 'create' ? (
                    <>
                        <Button variant="light" onClick={() => setView('list')}>
                            {intl.formatMessage(C.cancel)}
                        </Button>
                        <Button variant="primary" onClick={handleCreate} disabled={!newTagName || submitting}>
                            {intl.formatMessage(submitting ? C.saving : C.create)}
                        </Button>
                    </>
                ) : (
                    <Button variant="light" onClick={onHide}>
                        {intl.formatMessage(C.close)}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default TagManager
